/**
 * @module app.core
 */

import { autoinject, bindable, bindingMode } from 'aurelia-framework';
import { I18N } from 'aurelia-i18n';
import { ComponentBase, IForm, SortDirection, Sorting } from 'app/shared/lib/essette-site';
import { DocumentsService } from 'app/core/services/documents-service';
import { IDocumentsConfig, INotesConfig } from 'app/core/models/essette-interfaces';
import { SortChangeEvent } from 'app/shared/attributes/sortable-attribute';
import { EssPagerCustomElement } from 'app/shared/elements/ess-pager';
import { defer } from 'app/shared/lib/decorators';
import { apiCall } from 'app/shared/lib/essette-api';
import { IQueryLoadOptions } from 'app/shared/lib/api/api-types';
import { confirmation } from 'app/shared/elements/ess-confirm';
import { validate } from 'app/shared/elements/ess-rule';
import { IPopup } from 'app/shared/elements/popup-base';
import { UploadDocument } from 'app/core/components/documents/upload-document';
import { FormCancelCustomEvent, FormSuccessCustomEvent } from 'app/shared/elements/forms/ess-form';
import { openNotes } from 'app/core/components/notes/notes-grid';
import { IDialogOptions } from 'app/shared/elements/ess-global-modal';
import { DownloadManager } from 'app/shared/lib/download/manager';
import { getDocumentIcon } from 'app/shared/lib/utilities';
import { Toaster } from 'app/shared/lib/toaster';
import * as Core from 'app/core/models/essette-api';
import { createDocumentsCache, IDocumentsCache } from 'app/core/caches/documents-cache';
import { UseContainerCache } from 'app/shared/lib/cache/use-container-cache';
import { EssTableCustomElement } from 'app/shared/elements/ess-table';

@autoinject()
@UseContainerCache({ cacheFactory: createDocumentsCache })
export class DocumentsGrid extends ComponentBase {

    ////****************** Fields and Properties ************************************////
    @bindable()
    protected cacheKey: string | null = null;
    declare cache: IDocumentsCache;

    @bindable({ defaultBindingMode: bindingMode.toView, changeHandler: 'reload' })
    public targetId: string | null = null;

    @bindable({ defaultBindingMode: bindingMode.oneTime })
    public documentsConfig: IDocumentsConfig = {};

    @bindable({ defaultBindingMode: bindingMode.toView })
    public showInactive: boolean = false;

    @bindable({ defaultBindingMode: bindingMode.toView })
    public readonly: boolean = false;

    @bindable({ defaultBindingMode: bindingMode.toView })
    public allowDeactivate: boolean = true;

    @bindable({ defaultBindingMode: bindingMode.toView })
    public includeInactive: boolean = false;

    @bindable({ defaultBindingMode: bindingMode.toView })
    public pageSize: number = 25;

    protected sorting = new Sorting();
    protected defaultSorting = new Sorting();

    protected hasDownloadDocuments: boolean = false;

    protected showNotesPopup: boolean = false;

    protected validate = validate;
    protected $isValid: boolean = false;
    protected modalOpened = false;

    protected openNotes = openNotes;

    protected faxNotesConfig: INotesConfig = {
        serviceRootUrl: 'fm/faxes',
        targetType: 'FM.FaxDocument',
        moduleCode: 'FM',
        excludeSystem: false
    };

    protected getDocumentIcon = getDocumentIcon;

    ////****************** Element References ***************************************////

    protected $pager!: EssPagerCustomElement;
    protected addDocumentModal!: IPopup;
    protected uploadDocument!: UploadDocument;
    protected $documentsTable!: EssTableCustomElement<Core.Note>;

    ////****************** Element References ***************************************////


    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        private readonly service: DocumentsService,
        private readonly downloadManager: DownloadManager,
        private readonly toaster: Toaster,
        private readonly i18n: I18N,
        ...rest: any[]) {

        super(...rest);

        this.defaultSorting.add({
            reference: 'attachment.metadata.created.timestamp',
            direction: SortDirection.Ascending
        });
    }

    protected bind(): void {
        this.service.configure(this.documentsConfig);
        this.downloadManager.configureService(this.documentsConfig);

        this.checkPermissions();

        // Check if cached document data is still fresh to use and related to target id
        if(this.cache.targetId === this.targetId && this.cache.isFresh('documents')) {
            if(!!this.cache.sorting) {
                this.sorting = this.cache.sorting;
            }
            if(this.cache.includeInactive != null) {
                this.showInactive = this.cache.includeInactive;
            }
            this.$pager.page = this.cache.page || 0;
        } else {
            this.deferReload();
        }
    }

    protected attached(): void {
        if(this.sorting.entries.length > 0) {
            this.$documentsTable.applySorting(this.sorting);
        }
    }

    ////****************** Events ***************************************************////
    protected handleDocumentSaved(event: FormSuccessCustomEvent): void {
        this.closeModal(event.detail.form);
        this.reload(true);
    }

    protected handleUploadCancel(event: FormCancelCustomEvent): void {
        this.closeModal(event.detail.form);
    }

    protected handleDownloadFile(event: MouseEvent, document: Core.Document): void {
        this.downloadManager.configureService(this.documentsConfig);
        this.downloadManager.handleDownloadFile(event, document, false);
    }

    protected shouldView(document: Core.Document): void {
        this.downloadManager.shouldView(document);
    }


    ////****************** Functions ************************************************////
    private async checkPermissions(): Promise<void> {
        await this.accountContext.load();
        if(this.documentsConfig?.targetType === 'CM.Case') {
            this.hasDownloadDocuments = this.accountContext.hasPermission('CM.CaseDocumentAttachment.Download');
        }
        if(this.documentsConfig?.targetType === 'UM.Auth') {
            this.hasDownloadDocuments = this.accountContext.hasPermission('UM.AuthDocumentAttachment.Download');
        }
        this.showNotesPopup = this.accountContext.hasRouteAccess('fm.queues.fax.notes');
    }

    private openModal(): void {
        this.modalOpened = true;
        this.addDocumentModal.open();
    }

    private closeModal(form: IForm): void {
        this.addDocumentModal.close();
        if(form.reset) {
            form.reset();
        }
    }

    protected getSourceIcon(code: string): string | null {
        if(!code) { return null; }
        code = code.toLowerCase();
        if(code.startsWith('upload')) { return 'ui:icons.upload'; }
        if(code.startsWith('fax')) { return 'ui:icons.fax'; }
        return null;
    }

    public async reload(resetPage: boolean = false): Promise<void> {
        if(resetPage && this.$pager) this.$pager.updatePage(0);
        await this.loadDocuments();
    }

    protected targetIdChanged(): void {
        this.deferReload();
    }

    protected pageSizeChanged(): void {
        this.deferReload();
    }

    @defer
    protected deferReload(): void {
        this.reload();
    }

    protected async applySort(event: SortChangeEvent): Promise<void> {
        const { property, direction } = event.detail;
        const { sorting } = this;
        if(direction === null) {
            sorting.remove(property);
        } else {
            const sortDirection = direction === 'asc' ? SortDirection.Ascending : SortDirection.Descending;

            if(sorting.entries.findIndex(entry => entry.reference === property && entry.direction === sortDirection) > -1) {
                return;
            }
            else {
                sorting.add({
                    reference: property,
                    direction: sortDirection
                });
            }
        }
        await this.reload();
    }

    protected openNotePopup(document: Core.Document): void {
        if(!document.id || !document.queue?.id) {
            return;
        }

        const dialogOptions: IDialogOptions = {
            width: '10',
            height: '11',
            helpTopic: 'um.auth.document-fax-notes',
            title: {
                key: 'fm:routes.fax-notes.$title'
            }
        };

        this.openNotes(document.id, this.faxNotesConfig, dialogOptions, false, false, false, false, true);
    }

    ////****************** Service Calls ********************************************////
    @apiCall
    public async activateAttachment(attachment: Core.DocumentAttachment): Promise<void> {
        if(!attachment || !attachment.id) { return; }

        if(attachment.isActive) {
            const result = await confirmation.showCustom(
                this.i18n.tr('ui:messages.actions.deactivate', { what: this.i18n.tr('core:document.$') }),
                attachment.$display,
                confirmation.options.YesNo
            );
            if(result.dialogResponse === confirmation.options.NO) return;
        }

        let isSuccessful = false;
        try {
            this.state.beginSave();
            const response = await this.service.activateAttachment(attachment.id, !attachment.isActive);
            isSuccessful = response.isSuccessful;
            await this.reload();
        } catch(error) {
            throw error;
        } finally {
            this.toaster.standard.activate('core:document.$', !attachment.isActive, isSuccessful);
            this.state.endSave();
        }
    }

    @apiCall
    private async loadDocuments(): Promise<void> {
        if(!this.targetId) return;
        const options: Partial<IQueryLoadOptions> = {
            sorting: this.sorting.entries.length ? this.sorting : this.defaultSorting,
            top: this.pageSize,
            page: this.$pager?.page || 0,
            includeInactive: this.showInactive
        };

        const includes = [
            'attachment',
            'attachment.member',
            'attachment.metadata.created.byUser',
            'category',
            'flags',
            'mediaType',
            'notes',
            'source'
        ];

        const fields = [
            'category.description',
            'attachment.metadata.created.byUser.name.full',
            'attachment.metadata.created.timestamp',
            'description',
            'display',
            'file.name',
            'file.size',
            'isFax',
            'mediaType.description',
            'notes.id',
            'queue.id',
            'source.code',
            'source.description',
            'summary'
        ];

        if(this.showInactive) {
            includes.push('attachment.metadata.activeChanged.byUser');
            fields.push(
                'attachment.metadata.activeChanged.byUser.name.first',
                'attachment.metadata.activeChanged.byUser.name.full',
                'attachment.metadata.activeChanged.byUser.name.last',
                'attachment.metadata.activeChanged.timestamp'
            );
        }

        if(this.hasDownloadDocuments) {
            fields.push('mediaType.code', 'mediaType.fileExtension');
        }

        if(this.documentsConfig.hasTasks) {
            includes.push('metadata.created.byTask', 'metadata.created.byTask.attachedTo.type.designation');
            fields.push(
                'metadata.created.byTask.attachedTo.type.designation.description',
                'metadata.created.byTask.description'
            );
        }

        try {
            this.state.beginLoad();
            const response = await this.service.loadDocumentsByTarget(this.targetId, options, includes, fields);
            this.cache.documents = response.results;
            this.cache.targetId = this.targetId;
            this.cache.includeInactive = this.showInactive;
            this.cache.sorting = this.sorting;
            this.cache.page = this.$pager?.page || 0;
            this.cache.resultCount = response.count || 0;
        }
        catch(error) {
            throw error;
        }
        finally {
            this.state.endLoad();
        }
    }

}

